import { CheckCircle, X, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useUpdateKycStatus } from '../hooks/useUpdateKycStatus';
import { useVerifyKycDocument } from '../hooks/useVerifyKycDocument';
import { formatDateTime } from 'utils/dateFormatter';

const KycManagementModal = ({ isOpen, onClose, seller }) => {
  const [activeTab, setActiveTab] = useState('status');
  const [kycStatus, setKycStatus] = useState(seller?.kyc?.status || '');
  const [note, setNote] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentStatus, setDocumentStatus] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const { mutate: updateKycStatus, isPending: isUpdatingKyc } = useUpdateKycStatus();
  const { mutate: verifyDocument, isPending: isVerifyingDoc } = useVerifyKycDocument();

  if (!isOpen) return null;

  const handleUpdateKycStatus = (e) => {
    e.preventDefault();
    updateKycStatus(
      {
        sellerId: seller._id,
        status: kycStatus,
        note: note || undefined,
      },
      {
        onSuccess: () => {
          onClose();
          setNote('');
        },
      }
    );
  };

  const handleVerifyDocument = (doc, index) => {
    setSelectedDocument({ doc, index });
    setDocumentStatus(doc.verificationStatus);
    setRejectionReason('');
  };

  const handleSubmitDocumentVerification = (e) => {
    e.preventDefault();
    verifyDocument(
      console.log({ selectedDocument })

      // {
      //   sellerId: seller._id,
      //   documentIndex: selectedDocument.index,
      //   verificationStatus: documentStatus,
      //   rejectionReason: documentStatus === 'rejected' ? rejectionReason : undefined,
      // },
      // {
      //   onSuccess: () => {
      //     setSelectedDocument(null);
      //     setDocumentStatus('');
      //     setRejectionReason('');
      //   },
      // }
    );
  };

  const kycStatusOptions = [
    { value: 'pending', label: 'Pending', description: 'Awaiting review' },
    { value: 'under_review', label: 'Under Review', description: 'Currently being reviewed' },
    { value: 'approved', label: 'Approved', description: 'KYC approved' },
    { value: 'rejected', label: 'Rejected', description: 'KYC rejected' },
    { value: 'expired', label: 'Expired', description: 'KYC documents expired' },
  ];

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto p-4'>
      <div className='bg-card border border-border rounded-lg shadow-lg w-full max-w-3xl my-8'>
        {/* Header */}
        <div className='flex items-center justify-between p-6 border-b border-border'>
          <h2 className='text-lg font-semibold text-foreground'>KYC Management</h2>
          <button
            onClick={onClose}
            className='text-muted-foreground hover:text-foreground transition-colors'
            disabled={isUpdatingKyc || isVerifyingDoc}
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className='border-b border-border px-6'>
          <nav className='-mb-px flex space-x-8'>
            {['status', 'documents'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors capitalize ${
                  activeTab === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className='p-6 max-h-[60vh] overflow-y-auto'>
          {activeTab === 'status' && (
            <form onSubmit={handleUpdateKycStatus} className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-foreground mb-2'>Current KYC Status</label>
                <div className='space-y-2'>
                  {kycStatusOptions.map((option) => (
                    <label
                      key={option.value}
                      className='flex items-start p-3 border border-border rounded-md cursor-pointer hover:bg-accent transition-colors'
                    >
                      <input
                        type='radio'
                        name='kycStatus'
                        value={option.value}
                        checked={kycStatus === option.value}
                        onChange={(e) => setKycStatus(e.target.value)}
                        className='mt-1'
                        required
                      />
                      <div className='ml-3'>
                        <div className='text-sm font-medium text-foreground capitalize'>{option.label}</div>
                        <div className='text-xs text-muted-foreground'>{option.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor='note' className='block text-sm font-medium text-foreground mb-2'>
                  Note (Optional)
                </label>
                <textarea
                  id='note'
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary'
                  placeholder='Add a note about this KYC status change...'
                />
              </div>

              <div className='flex justify-end gap-3 pt-4'>
                <button
                  type='button'
                  onClick={onClose}
                  className='px-4 py-2 text-sm border border-border rounded-md bg-card hover:bg-accent transition-colors'
                  disabled={isUpdatingKyc}
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={isUpdatingKyc || !kycStatus}
                  className='px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {isUpdatingKyc ? 'Updating...' : 'Update KYC Status'}
                </button>
              </div>
            </form>
          )}

          {activeTab === 'documents' && (
            <div className='space-y-4'>
              {seller?.kyc?.documents?.length > 0 ? (
                seller.kyc.documents.map((doc, index) => (
                  <div key={doc._id} className='border border-border rounded-lg p-4'>
                    <div className='flex items-start justify-between mb-3'>
                      <div className='flex-1'>
                        <div className='flex items-center gap-2'>
                          <h3 className='font-medium text-sm capitalize'>{doc.type?.replace('_', ' ')}</h3>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${
                              doc.verificationStatus === 'verified'
                                ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                                : doc.verificationStatus === 'rejected'
                                ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                                : 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
                            }`}
                          >
                            {doc.verificationStatus}
                          </span>
                        </div>
                        <p className='text-xs text-muted-foreground mt-1'>Uploaded: {formatDateTime(doc.uploadedAt)}</p>
                        {doc.verifiedAt && (
                          <p className='text-xs text-muted-foreground'>Verified: {formatDateTime(doc.verifiedAt)}</p>
                        )}
                        {doc.rejectionReason && (
                          <p className='text-xs text-red-600 mt-1'>Reason: {doc.rejectionReason}</p>
                        )}
                      </div>
                      <div className='flex gap-2'>
                        <a
                          href={doc.documentUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='px-3 py-1 text-xs bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors'
                        >
                          View
                        </a>
                        <button
                          onClick={() => handleVerifyDocument(doc, index)}
                          className='px-3 py-1 text-xs border border-border rounded-md bg-card hover:bg-accent transition-colors'
                        >
                          Verify
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className='text-sm text-muted-foreground text-center py-8'>No documents uploaded</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Document Verification Modal */}
      {selectedDocument && (
        <div className='fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm'>
          <div className='bg-card border border-border rounded-lg shadow-lg w-full max-w-md mx-4'>
            <div className='flex items-center justify-between p-6 border-b border-border'>
              <h3 className='text-lg font-semibold text-foreground'>Verify Document</h3>
              <button
                onClick={() => setSelectedDocument(null)}
                className='text-muted-foreground hover:text-foreground transition-colors'
                disabled={isVerifyingDoc}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmitDocumentVerification} className='p-6 space-y-4'>
              <div>
                <label className='block text-sm font-medium text-foreground mb-3'>Verification Status</label>
                <div className='space-y-2'>
                  <label className='flex items-center p-3 border border-border rounded-md cursor-pointer hover:bg-accent transition-colors'>
                    <input
                      type='radio'
                      name='docStatus'
                      value='verified'
                      checked={documentStatus === 'verified'}
                      onChange={(e) => setDocumentStatus(e.target.value)}
                      className='mr-3'
                      required
                    />
                    <CheckCircle size={16} className='text-green-600 mr-2' />
                    <span className='text-sm font-medium text-foreground'>Verified</span>
                  </label>
                  <label className='flex items-center p-3 border border-border rounded-md cursor-pointer hover:bg-accent transition-colors'>
                    <input
                      type='radio'
                      name='docStatus'
                      value='rejected'
                      checked={documentStatus === 'rejected'}
                      onChange={(e) => setDocumentStatus(e.target.value)}
                      className='mr-3'
                      required
                    />
                    <XCircle size={16} className='text-red-600 mr-2' />
                    <span className='text-sm font-medium text-foreground'>Rejected</span>
                  </label>
                </div>
              </div>

              {documentStatus === 'rejected' && (
                <div>
                  <label htmlFor='rejectionReason' className='block text-sm font-medium text-foreground mb-2'>
                    Rejection Reason (Required)
                  </label>
                  <textarea
                    id='rejectionReason'
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={3}
                    className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary'
                    placeholder='Enter reason for rejection...'
                    required
                  />
                </div>
              )}

              <div className='flex justify-end gap-3 pt-4'>
                <button
                  type='button'
                  onClick={() => setSelectedDocument(null)}
                  className='px-4 py-2 text-sm border border-border rounded-md bg-card hover:bg-accent transition-colors'
                  disabled={isVerifyingDoc}
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={isVerifyingDoc || !documentStatus}
                  className='px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {isVerifyingDoc ? 'Submitting...' : 'Submit Verification'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default KycManagementModal;
